/**
 * Costco price adjustments: if an item's price drops within 30 days of buying it, the service
 * desk refunds the difference. This lists recent Costco purchases with their deadline, and,
 * where you've noted a lower current price in the catalog, what you'd get back.
 */
import type { Ledger } from "./ledger.ts";
import type { Micro } from "../money.ts";
import { catalogKey, type CatalogNote, type IsoDate } from "../types.ts";

export const ADJUSTMENT_DAYS = 30;

export interface WatchItem {
  itemId: string;
  itemNo: string;
  name: string;
  store: string;
  date: IsoDate;
  /** What you paid for it (before tax), in cents. */
  paidCents: number;
  /** The last day the store will adjust the price. */
  deadline: IsoDate;
  daysLeft: number;
  /** A lower price you noted (catalog), if any. */
  nowCents: number | null;
  /** Price difference plus the tax charged on it, when lower. */
  refundMicro: Micro;
  /** A note on the item mentions a price adjustment (like the Sheet's notes did). */
  flagged: boolean;
}

function addDays(iso: IsoDate, n: number): IsoDate {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function daysBetween(a: IsoDate, b: IsoDate): number {
  return Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000);
}

export function priceWatch(
  ledger: Ledger,
  notes: Record<string, CatalogNote> | undefined,
  today: IsoDate,
): WatchItem[] {
  const out: WatchItem[] = [];
  for (const c of ledger.items) {
    const it = c.item;
    if (!/costco/i.test(it.store) || !it.itemNo || it.priceCents <= 0) continue;
    const deadline = addDays(it.date, ADJUSTMENT_DAYS);
    const daysLeft = daysBetween(today, deadline);
    if (daysLeft < 0) continue;
    const note = notes?.[catalogKey(it)];
    const now = note?.priceNowCents ?? null;
    const paid = it.priceCents + it.couponCents;
    const diff = now !== null && now < paid ? paid - now : 0;
    out.push({
      itemId: it.id,
      itemNo: it.itemNo,
      name: it.name,
      store: it.store,
      date: it.date,
      paidCents: paid,
      deadline,
      daysLeft,
      nowCents: now,
      refundMicro: diff * 10_000 + diff * (c.taxBp + c.tipBp),
      flagged: /price adjust/i.test(it.note),
    });
  }
  return out.sort((a, b) => b.refundMicro - a.refundMicro || a.daysLeft - b.daysLeft);
}

/** Text to show at the service desk for the items with a lower price. */
export function adjustmentRequest(items: readonly WatchItem[]): string {
  const rows = items.filter((i) => i.refundMicro > 0);
  if (!rows.length) return "";
  const money = (c: number) => `$${(c / 100).toFixed(2)}`;
  return [
    "Price adjustment request",
    ...rows.map(
      (i) =>
        `#${i.itemNo} ${i.name}: bought ${i.date} for ${money(i.paidCents)}, now ${money(i.nowCents!)}`,
    ),
  ].join("\n");
}
